import type { IPersonalizationStorageAdapter } from '../types';

/**
 * In-memory storage adapter for testing or stateless environments.
 *
 * All data is stored in memory and lost when the instance is destroyed.
 * Useful for:
 * - Unit tests
 * - Server-side rendering
 * - Temporary/stateless tracking scenarios
 *
 * @class MemoryPersonalizationStorageAdapter
 * @implements {IPersonalizationStorageAdapter}
 *
 * @example
 * // Testing
 * const storage = new MemoryPersonalizationStorageAdapter();
 * const client = new PersonalizationClient({
 *   storage,
 *   debug: true,
 *   // ... other config
 * });
 *
 * @example
 * // SSR with no persistence
 * const storage = new MemoryPersonalizationStorageAdapter();
 * // Data will be lost after request completes
 */
export class MemoryPersonalizationStorageAdapter implements IPersonalizationStorageAdapter {
  private actorId: string | null = null;
  private sessionId: string | null = null;
  private sessionTimestamp: number | null = null;

  getActorId(): string | null {
    return this.actorId;
  }

  setActorId(actorId: string): void {
    this.actorId = actorId;
  }

  removeActorId(): void {
    this.actorId = null;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  removeSessionId(): void {
    this.sessionId = null;
  }

  getSessionTimestamp(): number | null {
    return this.sessionTimestamp;
  }

  setSessionTimestamp(timestamp: number): void {
    this.sessionTimestamp = timestamp;
  }

  removeSessionTimestamp(): void {
    this.sessionTimestamp = null;
  }
}
